import React, { useState, useMemo, useCallback } from 'react';
import { ResponsiveContainer, BarChart, Bar, CartesianGrid, XAxis, YAxis, Legend, Tooltip, ReferenceArea } from 'recharts';
import { Legend as CustomLegend, Tick as CustomTick } from '../components';
import { getColorByIndex } from '../utils';
import { BorderedBar, Cursor, ReferenceAreaShape } from './components';
import { useTheme } from 'styled-components';
import { getColorShade } from '../../../../src/styled/utils';
import { tooltipFormatter as defaultTooltipFormatter } from './utils';
const scaleYAxis = (dataMax) => Math.floor(dataMax * 1.1); // for padding from top in CartesianGrid
const DOMAIN = [0, scaleYAxis];
const BAR_CHART_MARGIN = { top: 10 };
const DEFAULT_BAR_SIZE = 23;
export const ColumnChart = ({ data, dataKeyColumns, dataKeyXAxis, barSize = DEFAULT_BAR_SIZE, barGap = 4, barBorderRadius = 2, onClickColumnGroup, chartColors: chartColorsProps, legendPosition = 'center', allowDecimalsYAxis = true, allowDecimalsXAxis = true, tickXFormatter, tickYFormatter, tooltipFormatter = defaultTooltipFormatter, ...props }) => {
    var _a;
    const theme = useTheme();
    const { chartColors: defaultChartColors } = theme;
    const [activeColumnGroup, setActiveColumnGroup] = useState(null);
    const [hoveredColumn, setHoveredColumn] = useState(null);
    const chartColors = chartColorsProps && chartColorsProps.length ? chartColorsProps : defaultChartColors;
    const { cartesianGridStroke, referenceAreaFill, tooltipHoverFill, mobileHoverFill } = useMemo(() => ({
        cartesianGridStroke: getColorShade('surface', 'outline')({ theme }),
        referenceAreaFill: getColorShade('onSurface', 'focused')({ theme }),
        tooltipHoverFill: getColorShade('onSurface', 'hover')({ theme }),
        mobileHoverFill: 'transparent'
    }), [theme]);
    const isTouchEnabled = useCallback(() => {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }, []);
    const handleSelectColumn = ({ activeTooltipIndex, activeLabel, activePayload }) => {
        if (onClickColumnGroup) {
            onClickColumnGroup({
                activeColumnIndex: activeTooltipIndex,
                activeLabel: activeLabel,
                activePayload: activePayload
            });
            if (activeColumnGroup && activeColumnGroup.activeTooltipIndex === activeTooltipIndex) {
                setActiveColumnGroup(null);
            }
            else if (hoveredColumn &&
                (!activeColumnGroup ||
                    (activeColumnGroup && activeColumnGroup.activeTooltipIndex !== activeTooltipIndex))) {
                setActiveColumnGroup({ ...hoveredColumn, activeTooltipIndex });
            }
        }
    };
    return (React.createElement(ResponsiveContainer, null,
        React.createElement(BarChart, { data: data, barGap: barGap, ...props, margin: BAR_CHART_MARGIN, onClick: handleSelectColumn },
            activeColumnGroup && (React.createElement(ReferenceArea, { shape: React.createElement(ReferenceAreaShape, { activeColumnGroup: activeColumnGroup, referenceAreaFill: referenceAreaFill }) })),
            React.createElement(CartesianGrid, { vertical: false, strokeWidth: 0.5, fill: cartesianGridStroke }),
            React.createElement(XAxis, { dataKey: dataKeyXAxis, axisLine: false, tickLine: false, tick: React.createElement(CustomTick, { tickFormatter: tickXFormatter }), tickFormatter: tickXFormatter, allowDecimals: allowDecimalsXAxis }),
            React.createElement(YAxis, { axisLine: false, tickLine: false, allowDecimals: allowDecimalsYAxis, tickMargin: 20, domain: DOMAIN, tick: React.createElement(CustomTick, { tickFormatter: tickYFormatter }), tickFormatter: tickYFormatter }),
            React.createElement(Tooltip, { formatter: tooltipFormatter, cursor: onClickColumnGroup ? (React.createElement(Cursor, { setHoveredColumn: setHoveredColumn, fill: isTouchEnabled() ? mobileHoverFill : tooltipHoverFill })) : (false) }),
            React.createElement(Legend, { content: React.createElement(CustomLegend, { legendPosition: legendPosition }) }),
            typeof dataKeyColumns === 'string' && (React.createElement(Bar, { barSize: Array.isArray(barSize) ? ((_a = barSize[0]) !== null && _a !== void 0 ? _a : DEFAULT_BAR_SIZE) : barSize, dataKey: dataKeyColumns, shape: React.createElement(BorderedBar, { barBorderRadius: barBorderRadius }), fill: chartColors[0] })),
            Array.isArray(dataKeyColumns) &&
                dataKeyColumns.map(({ key, group }, index) => {
                    var _a;
                    return (React.createElement(Bar, { key: key, barSize: Array.isArray(barSize) ? ((_a = barSize[index]) !== null && _a !== void 0 ? _a : DEFAULT_BAR_SIZE) : barSize, stackId: group, dataKey: key, shape: React.createElement(BorderedBar, { barBorderRadius: barBorderRadius }), fill: getColorByIndex(chartColors, index) }));
                }))));
};
